import * as DateF from 'date-fns'

import { DurationFormatter } from '@blade/ui-kit'
import { DATE_FORMAT } from '@root/shared'
import { useDateFnsLocale, useTranslation } from '@root/shared/localization'
import { useCurrentDate } from '@root/shared/react'

import { LIMIT_ORDER_STATUS_DATE_MAP } from '../constants'

import type { LimitOrder } from '../../model'

export const useLimitOrderEndDateFormatted = () => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()
  const now = useCurrentDate({ updateInterval: 'minute' })

  return {
    /**
     * Formats end date for a given order.
     *
     * - For closed orders:
     *   - "Expired at: dd.mm.yyyy" for expired orders.
     *   - "Cancelled at: dd.mm.yyyy" for cancelled orders.
     *   - "Completed at: dd.mm.yyyy" for completed orders.
     * - For expiring orders:
     *   - "Expires date: dd.mm.yyyy HH:mm" (If the expiration date is more than 24 hours away).
     *   - "Expires in X hours" (if more than 1 hour).
     *   - "Expires in Y minutes" (if less than 1 hour).
     *   - "Expires in Z seconds" (if less than 1 minute).
     *
     * @returns {string} A formatted string representing the expiration status.
     */
    formatEndDate: (order: {
      status: LimitOrder.Status
      closedAt: Date | undefined
      deadlineAt: Date
    }): string => {
      // Closed orders
      if (order.closedAt) {
        const closedAtFormatted = DateF.format(
          order.closedAt,
          DATE_FORMAT.dd_MM_yy,
        )
        const dateLabel = t(LIMIT_ORDER_STATUS_DATE_MAP[order.status])

        return `${dateLabel}: ${closedAtFormatted}`
      }

      // Expiring orders
      const expiresAt = order.deadlineAt
      const diffHr = DateF.differenceInHours(expiresAt, now)
      const diffSec = DateF.differenceInSeconds(expiresAt, now)
      const expiresAtFormatted = DateF.format(
        expiresAt,
        DATE_FORMAT.dd_MM_yy__HH_mm,
      )

      if (diffSec <= 0) {
        return `${t('trade.expired-at')}: ${expiresAtFormatted}`
      }
      if (diffHr >= 24) {
        return `${t(LIMIT_ORDER_STATUS_DATE_MAP['active'])}: ${expiresAtFormatted}`
      }

      const formatted = DurationFormatter.formatDurationSingleUnit(
        DateF.intervalToDuration({ start: now, end: expiresAt }),
        { locale: locale },
      )

      return t('trade.expires-in', { interval: formatted })
    },
  }
}
